import { computed, ref, watch, onUnmounted, type Ref } from "vue"
import { clamp } from "@/utils/math"
import { useSpring, type SpringConfig, type SpringPreset } from "./use-spring"
import type { LetterformCoords } from "./use-letterform"
import type { LetterMasters } from "@/types/letterform"

// Ambient "pull" interaction for variable letterforms: while the pointer is
// over the letter, the linked/gapped anchor pairs lean toward the cursor on a
// spring, capped at a maximum travel distance, then settle back. Horizontal
// (all-X) groups lean on X toward the cursor's X; vertical (all-Y) groups lean
// on Y toward the cursor's Y.
//
// Only the pairs move. A "pair" is a connected component (via union-find) over
// the masters' `minGaps` and `linkedPairs` edges. Each component shares a single
// offset, computed from the component's midpoint, so the space between its
// members is preserved exactly — the group can lean toward the cursor but its
// members never spread or pinch relative to one another. Every anchor that is
// not part of a pairing stays fixed.
//
// Edge groups (touching the outer frame on their axis) stay pinned so the
// silhouette's bounds hold. Interior Y groups are additionally clamped to the
// container's vertical bounds minus a padding margin, so they never drift out
// of the letter.

export type LetterformPullOptions = {
  // Base, screen-space interpolation result to deform (reactive).
  base: Ref<LetterformCoords>
  // The letter definition — supplies the minGaps / linkedPairs that define pairs.
  masters: Ref<LetterMasters>
  // Container width in px — used to derive sensible defaults (reactive).
  width: Ref<number>
  // Container height in px — used for vertical travel/padding/edge logic (reactive).
  height: Ref<number>
  // Whether the interaction is enabled at all (e.g. the `interactive` prop).
  enabled: Ref<boolean>
  // Maximum horizontal travel for any anchor, in px. Defaults to width * 0.08.
  maxPull?: Ref<number | undefined>
  // Falloff radius in px — anchors beyond this barely move. Defaults to width * 0.25.
  pullRadius?: Ref<number | undefined>
  // Spring feel for the lag/settle/return.
  preset?: SpringPreset
}

const EPSILON = 0.05

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  )
}

export function useLetterformPull(options: LetterformPullOptions) {
  const { base, masters, width, height, enabled, preset = "gentle" } = options

  const { config } = useSpring(preset)
  const { stiffness, damping, mass } = config as SpringConfig

  // Pointer state, container-relative.
  const cursorX = ref(0)
  const cursorY = ref(0)
  const hovering = ref(false)

  // The only anchors that move: connected components of 2+ single-axis members
  // over the minGaps / linkedPairs edges. Each entry is one rigid group: its
  // member indices plus the axis they all share ("x" leans horizontally toward
  // the cursor, "y" vertically). Recomputed only when the masters (or their
  // axis map) change.
  const groups = computed<{ members: number[]; axis: "x" | "y" }[]>(() => {
    const { axes } = base.value
    const { minGaps, linkedPairs } = masters.value
    const n = axes.length
    if (n === 0) return []

    // Union-find over coordinate indices.
    const parent = Array.from({ length: n }, (_, i) => i)
    const find = (i: number): number => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]]
        i = parent[i]
      }
      return i
    }
    const union = (a: number, b: number) => {
      const ra = find(a)
      const rb = find(b)
      if (ra !== rb) parent[ra] = rb
    }

    for (const gap of minGaps ?? []) union(gap.indexA, gap.indexB)
    for (const link of linkedPairs ?? []) union(link.source, link.target)

    // Collect members per root, then keep only multi-member, all-X components.
    const byRoot = new Map<number, number[]>()
    for (let i = 0; i < n; i++) {
      const r = find(i)
      const members = byRoot.get(r)
      if (members) members.push(i)
      else byRoot.set(r, [i])
    }

    const result: { members: number[]; axis: "x" | "y" }[] = []
    for (const members of byRoot.values()) {
      if (members.length < 2) continue
      // A component must be single-axis to share one offset; keep all-X or
      // all-Y groups and tag them, skip any (defensive) mixed component.
      const axis = axes[members[0]]
      if (members.some(i => axes[i] !== axis)) continue
      result.push({ members, axis })
    }
    return result
  })

  // Per-group physics state (one pos/vel per rigid group).
  let positions: number[] = []
  let velocities: number[] = []
  // Per-coordinate offsets fed to the path builder; non-group anchors stay 0.
  let offsets: number[] = []
  const offsetsRef = ref<number[]>([])

  const reduced = prefersReducedMotion()

  const maxPull = computed(
    () => options.maxPull?.value ?? Math.max(width.value * 0.08, 0),
  )
  const pullRadius = computed(
    () => options.pullRadius?.value ?? Math.max(width.value * 1, 1),
  )
  // Vertical analogues, derived from height so motion scales with the letter.
  const maxPullY = computed(() => Math.max(height.value * 0.08, 0))
  const pullRadiusY = computed(() => Math.max(height.value * 1, 1))
  // Comfortable margin interior Y groups must keep from the top/bottom frame.
  const padding = computed(() => Math.max(height.value * 0.08, 0))

  function resize(coordCount: number, groupCount: number) {
    if (offsets.length !== coordCount) {
      offsets = new Array(coordCount).fill(0)
      offsetsRef.value = offsets.slice()
    }
    if (positions.length !== groupCount) {
      positions = new Array(groupCount).fill(0)
      velocities = new Array(groupCount).fill(0)
    }
  }

  let rafId: number | null = null
  let lastTime = 0

  // Edge anchors sit on the letter's outer frame (≈ 0 or ≈ the container size
  // on their axis). They stay pinned so the silhouette's bounds hold and only
  // interior anchors pull. For X that frame is the width; for Y the height.
  function isEdge(baseVal: number, axis: "x" | "y"): boolean {
    const extent = axis === "x" ? width.value : height.value
    const eps = Math.max(1, extent * 0.005)
    return baseVal <= eps || baseVal >= extent - eps
  }

  function targetFor(baseVal: number, axis: "x" | "y"): number {
    if (!hovering.value) return 0
    const cursor = axis === "x" ? cursorX.value : cursorY.value
    const maxTravel = axis === "x" ? maxPull.value : maxPullY.value
    const radius = axis === "x" ? pullRadius.value : pullRadiusY.value
    const raw = cursor - baseVal
    const weight = Math.exp(-((raw / radius) ** 2))
    return clamp(raw, -maxTravel, maxTravel) * weight
  }

  function step(time: number) {
    const { coords } = base.value
    const grps = groups.value
    resize(coords.length, grps.length)

    // Frame-rate-independent, sub-stepped integration for stability.
    const dt = lastTime ? Math.min((time - lastTime) / 1000, 0.05) : 1 / 60
    lastTime = time
    const substeps = 4
    const sdt = dt / substeps

    // Nothing moves by default; only the rigid groups below write offsets.
    offsets.fill(0)

    let active = false
    for (let g = 0; g < grps.length; g++) {
      const { members, axis } = grps[g]

      // Pin a whole group to the frame if any member sits on the outer edge,
      // so the silhouette bounds hold. For Y groups this is the top/bottom edge.
      const pinned = members.some(i => isEdge(coords[i], axis))

      // One shared target from the group's midpoint keeps members in lockstep,
      // so the spacing between them is preserved exactly.
      const mid =
        members.reduce((sum, i) => sum + coords[i], 0) / members.length
      let target = pinned ? 0 : targetFor(mid, axis)

      // Interior Y groups stay within the container's vertical bounds, minus a
      // padding margin, so they never drift out of the letter. The shared
      // offset is clamped against the group's own extent; if the group is
      // taller than the available window it simply doesn't move.
      let lo = -Infinity
      let hi = Infinity
      if (axis === "y" && !pinned) {
        let minMember = Infinity
        let maxMember = -Infinity
        for (const i of members) {
          if (coords[i] < minMember) minMember = coords[i]
          if (coords[i] > maxMember) maxMember = coords[i]
        }
        lo = padding.value - minMember
        hi = height.value - padding.value - maxMember
        if (lo > hi) {
          lo = 0
          hi = 0
        }
        target = clamp(target, lo, hi)
      }

      let pos = positions[g]
      let vel = velocities[g]
      for (let s = 0; s < substeps; s++) {
        const force = (target - pos) * stiffness - vel * damping
        vel += (force / mass) * sdt
        pos += vel * sdt
      }
      // Hard-clamp Y groups to the bounds window even mid-spring (overshoot).
      if (axis === "y" && !pinned) pos = clamp(pos, lo, hi)
      positions[g] = pos
      velocities[g] = vel

      for (const i of members) offsets[i] = pos

      if (
        Math.abs(pos - target) > EPSILON ||
        Math.abs(vel) > EPSILON ||
        target !== 0
      ) {
        active = true
      }
    }

    offsetsRef.value = offsets.slice()

    if (active || hovering.value) {
      rafId = requestAnimationFrame(step)
    } else {
      stop()
    }
  }

  function start() {
    if (rafId !== null || reduced || !enabled.value) return
    lastTime = 0
    rafId = requestAnimationFrame(step)
  }

  function stop() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  // Public pointer handlers, fed container-relative coordinates by the host.
  function onPointerEnter(x: number, y: number) {
    if (reduced || !enabled.value) return
    cursorX.value = x
    cursorY.value = y
    hovering.value = true
    start()
  }
  function onPointerMove(x: number, y: number) {
    if (reduced || !enabled.value) return
    cursorX.value = x
    cursorY.value = y
    if (!hovering.value) hovering.value = true
    start()
  }
  function onPointerLeave() {
    hovering.value = false
    // Keep stepping (via start) so anchors spring back to rest, then settle.
    start()
  }

  // If the interaction is disabled mid-flight, relax and halt.
  watch(enabled, on => {
    if (!on) {
      hovering.value = false
      start()
    }
  })

  onUnmounted(stop)

  // Base coords + live offsets, ready to feed into buildPathD.
  const coords = computed(() => {
    const b = base.value.coords
    if (reduced || !enabled.value || offsetsRef.value.length !== b.length) {
      return b
    }
    return b.map((v, i) => v + (offsetsRef.value[i] ?? 0))
  })

  return { coords, onPointerEnter, onPointerMove, onPointerLeave }
}
